import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Palette, Sun, Moon, Cloud, Save, Check, Link2, Loader2,
  Instagram, Facebook, Plug, Unplug, ShieldCheck, AlertTriangle,
  Users, UserPlus, Trash2, KeyRound, X, Radio, Plus, UserRound, Camera, Phone, Sparkles, Eye, EyeOff, Copy, Music2, Bell, BellRing, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, setAuthToken } from "@/lib/api";
import { ensurePushSubscription } from "@/lib/chat";
import { useTheme, ACCENTS } from "@/context/ThemeContext";
import { useAccount } from "@/context/AccountContext";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { NAV } from "@/components/layout/Sidebar";
import { AvatarCropDialog } from "@/components/AvatarCropDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Einstellungen() {
  const { accountKey } = useOutletContext();
  const { mode, setMode, accent, setAccent } = useTheme();
  const { current } = useAccount();
  const { user } = useAuth();
  const [cloudLink, setCloudLink] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!accountKey) return;
    api.get("/accounts").then(({ data }) => {
      const acc = data.find((a) => a.key === accountKey);
      setCloudLink(acc?.cloud_link || "");
    });
  }, [accountKey]);

  const saveCloud = async () => {
    const value = cloudLink.trim();
    if (value && !/^https:\/\//i.test(value)) {
      toast.error("Bitte einen vollständigen https-Link eingeben");
      return;
    }
    setSaving(true);
    try {
      const provider = /sharepoint\.com/i.test(value) ? "sharepoint" : value ? "link" : "";
      await api.patch(`/accounts/${accountKey}`, { cloud_link: value, cloud_provider: provider });
      setCloudLink(value);
      toast.success(`Cloud-Quelle für ${current?.name || accountKey} gespeichert`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[900px] mx-auto animate-rise">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
        <Palette className="h-3.5 w-3.5" /> Einstellungen & Integrationen
      </p>
      <h1 className="font-head text-4xl font-bold text-neutral-900 tracking-tighter mt-1">Erscheinungsbild & Integrationen</h1>

      <ProfileCard />

      <PasswordCard />

      <PushSettingsCard />

      <VersionCard />

      <MetaConnectCard accountKey={accountKey} accountName={current?.name} />

      <SocialCredentialsCard isAdmin={user?.role === "admin"} />

      {user?.role === "admin" && <ChannelManagementCard />}

      {user?.role === "admin" && <UserManagementCard />}
      {user?.role === "admin" && <CreatorNavCard />}

      {/* Appearance / White-Label */}
      <section className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
        <h2 className="font-head font-semibold text-neutral-900">Erscheinungsbild</h2>
        <p className="text-sm text-neutral-500 mt-1">Wähle Arbeitsmodus und Akzentfarbe (White-Label für deine Marke).</p>

        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Modus</label>
          <div className="mt-2 inline-flex rounded-lg border border-neutral-200 p-1 bg-neutral-50">
            <button data-testid="mode-light" onClick={() => setMode("light")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${mode === "light" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}>
              <Sun className="h-4 w-4" /> Hell
            </button>
            <button data-testid="mode-dark" onClick={() => setMode("dark")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${mode === "dark" ? "bg-neutral-900 text-white" : "text-neutral-500"}`}>
              <Moon className="h-4 w-4" /> Dunkel
            </button>
          </div>
        </div>

        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Akzentfarbe</label>
          <div className="mt-2 flex flex-wrap gap-2.5">
            {Object.entries(ACCENTS).map(([key, a]) => (
              <button
                key={key}
                data-testid={`accent-${key}`}
                onClick={() => setAccent(key)}
                title={a.label}
                className={`h-10 w-10 rounded-full flex items-center justify-center transition ring-2 ring-offset-2 ${accent === key ? "ring-neutral-900" : "ring-transparent"}`}
                style={{ background: a.value }}
              >
                {accent === key && <Check className="h-4 w-4 text-white" />}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations / Cloud */}
      <section className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
        <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
          <Cloud className="h-5 w-5 text-neutral-400" /> Cloud-Speicher / Content-Quelle
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Hinterlege die eigene Cloud-Quelle für <span className="font-semibold text-neutral-800">{current?.name}</span>.
          SharePoint-Dateien, Uploads, Importe, Drop-Links und das Content-Archiv dieses Kanals verwenden ausschließlich diese Quelle.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800" data-testid="cloud-channel-badge">
          <Cloud className="h-3.5 w-3.5" /> Kanal: {current?.name || accountKey}
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              data-testid="cloud-link-input"
              value={cloudLink}
              onChange={(e) => setCloudLink(e.target.value)}
              placeholder="https://innovationshafen.sharepoint.com/sites/…"
              type="url"
              className="w-full h-11 pl-10 pr-3 rounded-lg border border-neutral-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
          <button data-testid="cloud-link-save" onClick={saveCloud} disabled={saving}
            className="h-11 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium px-5 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
          </button>
        </div>
        {cloudLink && (
          <a href={cloudLink} target="_blank" rel="noreferrer" data-testid="cloud-link-open"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900">
            <Link2 className="h-3.5 w-3.5" /> Quelle von {current?.name || accountKey} öffnen
          </a>
        )}
        <div className="mt-4 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-[13px] text-blue-900">
          <span className="font-semibold">Hinweis zum „Lernen am Content":</span> Die KI wird nicht dauerhaft auf einem Ordner trainiert.
          Du kannst aber pro Inhalt eine Datei (Video/Bild/PDF) hochladen oder einen Cloud-Link angeben — und die KI hilft beim
          Auslesen (Foto→Felder), bei Hooks/Captions und Content-Ideen. So nutzt du euer Material direkt im Workflow.
        </div>
      </section>
    </div>
  );
}

function CreatorNavCard() {
  const [paths, setPaths] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/creator-nav").then(({ data }) => {
      setPaths(Array.isArray(data.paths) ? data.paths : NAV.filter((n) => n.creator).map((n) => n.to));
    });
  }, []);
  const toggle = (to) => setPaths((p) => (p.includes(to) ? p.filter((x) => x !== to) : [...p, to]));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/creator-nav", { paths });
      toast.success("Creator-Sichtbarkeit gespeichert — gilt beim nächsten Laden der App");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  if (!paths) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="creator-nav-card">
      <h2 className="font-head font-semibold text-neutral-900">Creator-Sichtbarkeit</h2>
      <p className="text-sm text-neutral-400 mt-0.5">Wähle, welche Bereiche Creator (z. B. Mika, Amelie) in der Navigation sehen.</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {NAV.map((item) => (
          <label key={item.to} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-neutral-50 cursor-pointer">
            <input
              type="checkbox"
              checked={paths.includes(item.to)}
              onChange={() => toggle(item.to)}
              data-testid={`creator-nav-toggle-${item.to.replaceAll("/", "")}`}
              className="h-4 w-4 rounded accent-neutral-900"
            />
            <span className="text-sm text-neutral-700">{item.label}</span>
          </label>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="creator-nav-save"
        className="mt-4 h-10 px-5 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700 disabled:opacity-50"
      >
        {saving ? "Speichert…" : "Speichern"}
      </button>
    </div>
  );
}

function ProfileCard() {
  const { user, reload } = useAuth();
  const [form, setForm] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && !form) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        instagram: user.instagram || "",
        skills: (user.skills || []).join(", "),
        avatar_url: user.avatar_url || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!form) return null;

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Foto zu groß (max. 10 MB)");
      return;
    }
    setCropFile(file);
  };

  const uploadCropped = async (blob) => {
    setUploading(true);
    try {
      // Deploy-fest: Bild wird sofort im Profil (Datenbank) gespeichert
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
      const { data } = await api.patch("/auth/profile", { avatar_url: dataUrl });
      setForm((f) => ({ ...f, avatar_url: data.avatar_url || dataUrl }));
      setCropFile(null);
      await reload();
      toast.success("Profilbild gespeichert ✅");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Speichern fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", {
        name: form.name,
        phone: form.phone,
        instagram: form.instagram,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        avatar_url: form.avatar_url,
      });
      await reload();
      toast.success("Profil gespeichert ✅");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="profile-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <AvatarCropDialog file={cropFile} onCancel={() => setCropFile(null)} onCropped={uploadCropped} />
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <UserRound className="h-5 w-5 text-neutral-400" /> Mein Profil
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        Profilfoto und Kontaktdaten – sichtbar für dein Team, z. B. im Chat.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-6">
        <div className="flex flex-col items-center gap-3 shrink-0">
          <UserAvatar src={form.avatar_url} name={form.name} color={user?.avatar_color} className="h-24 w-24" textClass="text-2xl" />
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer">
            <input data-testid="profile-photo-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            Foto ändern
          </label>
        </div>
        <div className="flex-1 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Name</label>
            <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block flex items-center gap-1"><Phone className="h-3 w-3" /> Handynummer</label>
            <input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+49 170 1234567"
              className="w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block flex items-center gap-1"><Instagram className="h-3 w-3" /> Instagram-Profil</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">@</span>
              <input data-testid="profile-instagram-input" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                placeholder="dein.handle"
                className="w-full h-10 pl-8 pr-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block flex items-center gap-1"><Sparkles className="h-3 w-3" /> Skills (mit Komma trennen)</label>
            <input data-testid="profile-skills-input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
              placeholder="Reels, Schnitt, Fotografie"
              className="w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
          </div>
          <div className="sm:col-span-2">
            <button data-testid="profile-save-btn" onClick={save} disabled={saving || uploading}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-5 h-10 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Profil speichern
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [visible, setVisible] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (form.next.length < 12) {
      toast.error("Das neue Passwort muss mindestens 12 Zeichen haben");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("Die neuen Passwörter stimmen nicht überein");
      return;
    }
    if (form.current === form.next) {
      toast.error("Das neue Passwort muss sich vom aktuellen unterscheiden");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      if (data.access_token) setAuthToken(data.access_token);
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Passwort geändert. Andere Sitzungen wurden abgemeldet.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Passwort konnte nicht geändert werden");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, autoComplete) => (
    <div>
      <label htmlFor={`password-${key}`} className="text-xs font-semibold text-neutral-600 mb-1.5 block">
        {label}
      </label>
      <div className="relative">
        <input
          id={`password-${key}`}
          data-testid={`password-${key}`}
          type={visible[key] ? "text" : "password"}
          autoComplete={autoComplete}
          value={form[key]}
          onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
          className="w-full h-10 px-3 pr-10 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          required
        />
        <button
          type="button"
          onClick={() => setVisible((current) => ({ ...current, [key]: !current[key] }))}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-700"
          aria-label={`${label} ${visible[key] ? "verbergen" : "anzeigen"}`}
        >
          {visible[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <section data-testid="password-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-neutral-400" /> Passwort ändern
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        Verwende mindestens 12 Zeichen. Nach der Änderung werden andere bestehende Sitzungen ungültig.
      </p>
      <form onSubmit={submit} className="mt-5 grid sm:grid-cols-3 gap-4">
        {field("current", "Aktuelles Passwort", "current-password")}
        {field("next", "Neues Passwort", "new-password")}
        {field("confirm", "Neues Passwort bestätigen", "new-password")}
        <div className="sm:col-span-3">
          <button
            type="submit"
            data-testid="password-submit"
            disabled={saving || !form.current || !form.next || !form.confirm}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-5 h-10 rounded-lg hover:bg-neutral-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Passwort speichern
          </button>
        </div>
      </form>
    </section>
  );
}


function ChannelManagementCard() {
  const { accounts } = useAccount();
  const [form, setForm] = useState({ name: "", tagline: "", tone: "vibrant", accent: "#2563EB" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Bitte einen Kanal-Namen angeben");
    setBusy(true);
    try {
      const { data } = await api.post("/accounts", form);
      toast.success(`Kanal „${data.name}" angelegt — jetzt Creator zuweisen`);
      setForm({ name: "", tagline: "", tone: "vibrant", accent: "#2563EB" });
      setShowForm(false);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="channel-management-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
            <Radio className="h-5 w-5 text-neutral-400" /> Kanäle & Kunden-Accounts
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Jeder Kanal ist eine eigene, komplett getrennte Welt. Für Kunden: Kanal anlegen → Creator anlegen und nur diesen Kanal zuweisen — der Kunde sieht ausschließlich seine Inhalte.
          </p>
        </div>
        <button data-testid="new-channel-btn" onClick={() => setShowForm((s) => !s)}
          className="shrink-0 inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-3.5 py-2 rounded-lg hover:bg-neutral-800">
          <Plus className="h-4 w-4" /> Neuer Kanal
        </button>
      </div>

      {showForm && (
        <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="new-channel-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Kanal-Name (z. B. metzgerei.mueller)" className="h-10 px-3 rounded-lg border border-neutral-300 text-sm" />
            <input data-testid="new-channel-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="Kurzbeschreibung (z. B. Handwerk · Regional)" className="h-10 px-3 rounded-lg border border-neutral-300 text-sm" />
            <select data-testid="new-channel-tone" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}
              className="h-10 px-3 rounded-lg border border-neutral-300 text-sm bg-white">
              <option value="vibrant">Tonalität: lebendig & bürgernah</option>
              <option value="structured">Tonalität: strukturiert & hochwertig</option>
            </select>
            <div className="flex items-center gap-2">
              <input type="color" data-testid="new-channel-accent" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })}
                className="h-10 w-12 rounded-lg border border-neutral-300 cursor-pointer p-1 bg-white" />
              <span className="text-sm text-neutral-500">Kanal-Farbe</span>
            </div>
          </div>
          <button data-testid="create-channel-btn" onClick={create} disabled={busy}
            className="mt-3 inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Kanal anlegen
          </button>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {accounts.map((a) => (
          <div key={a.key} data-testid={`channel-row-${a.key}`} className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3">
            <span className="h-3 w-3 rounded-full shrink-0" style={{ background: a.accent }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-neutral-900">{a.name}</div>
              <div className="text-xs text-neutral-400 truncate">{a.tagline || "—"} · Schlüssel: {a.key}</div>
            </div>
            <button
              data-testid={`delete-channel-${a.key}`}
              title="Kanal löschen"
              onClick={async () => {
                if (!window.confirm(`Kanal „${a.name}" wirklich löschen?\n\nAlle Inhalte, Themen, Events und Brand-Assets dieses Kanals werden dauerhaft entfernt. Das kann nicht rückgängig gemacht werden.`)) return;
                try {
                  await api.delete(`/accounts/${a.key}`);
                  toast.success(`Kanal „${a.name}" gelöscht`);
                  window.location.reload();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
                }
              }}
              className="text-neutral-300 hover:text-rose-500 transition p-1.5"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function UserManagementCard() {
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", account_keys: [], role: "creator" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () => {
    api.get("/users").then(({ data }) => setUsers(data));
    api.get("/accounts").then(({ data }) => setAccounts(data));
  };
  useEffect(() => { load(); }, []);

  const toggleAcc = (key) =>
    setForm((f) => ({
      ...f,
      account_keys: f.account_keys.includes(key) ? f.account_keys.filter((k) => k !== key) : [...f.account_keys, key],
    }));

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 8) {
      toast.error("Name, E-Mail und Passwort (min. 8 Zeichen) erforderlich");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users", { ...form });
      toast.success(`${form.role === "admin" ? "Admin" : "Creator"} ${form.name} angelegt`);
      setCreated({ name: form.name, email: form.email.trim(), password: form.password, emailSent: data.email_sent, emailError: data.email_error });
      setForm({ name: "", email: "", password: "", account_keys: [], role: "creator" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Neues Passwort für ${u.name} (min. 8 Zeichen):`);
    if (!pw) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, { new_password: pw });
      toast.success("Passwort zurückgesetzt");
      setCreated({ name: u.name, email: u.email, password: pw, emailSent: data.email_sent, emailError: data.email_error });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.name} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Benutzer gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  return (
    <section data-testid="user-management-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <Users className="h-5 w-5 text-neutral-400" /> Team & Benutzer
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        Lege Zugänge für deine Creator an und weise ihnen Accounts zu. Creator sehen nur die zugewiesenen Marken.
      </p>

      {/* Create form */}
      <div className="mt-5 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3 flex items-center gap-1.5">
          <UserPlus className="h-3.5 w-3.5" /> Neuen Benutzer anlegen
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name" className="h-10 px-3 rounded-lg border border-neutral-300 text-sm" />
          <input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="E-Mail" className="h-10 px-3 rounded-lg border border-neutral-300 text-sm" />
          <input data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Start-Passwort (min. 8)" className="h-10 px-3 rounded-lg border border-neutral-300 text-sm" />
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-1.5">Rolle</div>
            <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="h-10 px-3 rounded-lg border border-neutral-300 text-sm bg-white w-full">
              <option value="creator">Creator — sieht nur zugewiesene Kanäle</option>
              <option value="admin">Admin — voller Zugriff auf alles</option>
            </select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-1.5">Zugewiesene Accounts{form.role === "admin" ? " (Admins sehen automatisch alle)" : ""}</div>
            <div className="flex flex-wrap gap-2">
              {accounts.map((a) => (
                <button key={a.key} data-testid={`assign-acc-${a.key}`} onClick={() => toggleAcc(a.key)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${form.account_keys.includes(a.key) ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-300"}`}>
                  {a.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button data-testid="create-user-btn" onClick={create} disabled={busy}
          className="mt-4 inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Benutzer anlegen
        </button>
      </div>

      {created && (
        <div data-testid="created-user-box" className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-sm font-bold text-emerald-800">✅ Zugangsdaten für {created.name}</div>
          {created.emailSent ? (
            <p className="text-xs text-emerald-700 mt-1">
              Eine E-Mail mit den Zugangsdaten wurde an <b>{created.email}</b> gesendet.
              Du kannst sie zusätzlich hier kopieren und z. B. per WhatsApp weitergeben:
            </p>
          ) : (
            <p data-testid="email-failed-warning" className="text-xs font-semibold text-rose-600 mt-1">
              ⚠️ Die E-Mail konnte NICHT zugestellt werden{created.emailError ? ` (${created.emailError})` : ""}.
              Bitte gib die Zugangsdaten manuell weiter — einfach unten kopieren und per WhatsApp senden:
            </p>
          )}
          <div className="mt-2 text-xs font-mono bg-white border border-emerald-200 rounded-lg p-2.5 text-neutral-700">
            Login: {window.location.origin}<br />E-Mail: {created.email}<br />Passwort: {created.password}
          </div>
          <div className="mt-2.5 flex gap-2">
            <button data-testid="copy-credentials-btn"
              onClick={async () => {
                const txt = `Dein Zugang zum Redaktionsboard:\nLogin: ${window.location.origin}\nE-Mail: ${created.email}\nPasswort: ${created.password}`;
                try { await navigator.clipboard.writeText(txt); toast.success("Zugangsdaten kopiert"); }
                catch { toast.info(txt, { duration: 12000 }); }
              }}
              className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
              Zugangsdaten kopieren
            </button>
            <button data-testid="dismiss-created-btn" onClick={() => setCreated(null)}
              className="text-xs font-medium px-3 py-2 rounded-lg text-emerald-700 hover:bg-emerald-100">
              Schließen
            </button>
          </div>
        </div>
      )}

      {/* User list */}
      <div className="mt-5 space-y-2">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3">
            <div className="h-9 w-9 rounded-full bg-neutral-100 flex items-center justify-center text-sm font-bold text-neutral-600 shrink-0">
              {(u.name || u.email)[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                {u.name}
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-neutral-100 text-neutral-500"}`}>
                  {u.role === "admin" ? "Admin" : "Creator"}
                </span>
              </div>
              <div className="text-xs text-neutral-400 truncate">{u.email} · {(u.account_keys || []).join(", ") || "keine Accounts"}</div>
            </div>
            {u.role !== "admin" && (
              <>
                <button onClick={() => resetPw(u)} title="Passwort zurücksetzen" className="text-neutral-400 hover:text-neutral-700 p-1.5">
                  <KeyRound className="h-4 w-4" />
                </button>
                <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} title="Löschen" className="text-neutral-400 hover:text-rose-500 p-1.5">
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function MetaConnectCard({ accountKey, accountName }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    if (!accountKey) return;
    setLoading(true);
    try {
      const { data } = await api.get("/meta/status", { params: { account: accountKey } });
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [accountKey]);

  useEffect(() => { load(); }, [load]);

  // Handle OAuth callback result (?meta=connected | error | connected_no_ig)
  useEffect(() => {
    const meta = params.get("meta");
    if (!meta) return;
    if (meta === "connected") toast.success("Instagram & Facebook erfolgreich verbunden");
    else if (meta === "fb_connected") toast.success("Facebook-Seite erfolgreich verbunden — Posts laufen jetzt auch auf Facebook ✅");
    else if (meta === "connected_no_ig") toast.warning("Facebook-Seite verbunden, aber kein Instagram-Business-Konto verknüpft.");
    else if (meta === "error") toast.error(`Verbindung fehlgeschlagen: ${params.get("msg") || "Unbekannter Fehler"}`);
    params.delete("meta"); params.delete("msg");
    setParams(params, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.post("/auth/meta/ticket", { account: accountKey });
      window.location.href = `${BACKEND_URL}/api/auth/meta/login?ticket=${encodeURIComponent(data.ticket)}`;
    } catch {
      toast.error("Verbindung konnte nicht gestartet werden");
    }
  };

  const connectFacebook = async () => {
    try {
      const { data } = await api.post("/auth/meta/ticket", { account: accountKey });
      window.location.href = `${BACKEND_URL}/api/auth/meta/fb-login?ticket=${encodeURIComponent(data.ticket)}`;
    } catch {
      toast.error("Facebook-Verbindung konnte nicht gestartet werden");
    }
  };

  const disconnect = async () => {
    setWorking(true);
    try {
      await api.post("/meta/disconnect", null, { params: { account: accountKey } });
      toast.success("Verbindung getrennt");
      setStatus({ connected: false });
    } catch {
      toast.error("Trennen fehlgeschlagen");
    } finally {
      setWorking(false);
    }
  };

  const connected = status?.connected;

  return (
    <section data-testid="meta-connect-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
            <span className="inline-flex">
              <Instagram className="h-5 w-5 text-pink-500" />
              <Facebook className="h-5 w-5 text-blue-600 -ml-1" />
            </span>
            Social-Media-Anbindung (Meta)
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Verbinde Instagram & Facebook für <span className="font-medium">{accountName || accountKey}</span> —
            für echtes Auto-Publishing und Live-Insights. Jede Marke wird einzeln verbunden.
          </p>
        </div>
        <span
          data-testid="meta-status-badge"
          className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
            connected ? "bg-green-50 text-green-700" : "bg-neutral-100 text-neutral-500"
          }`}
        >
          {connected ? <ShieldCheck className="h-3.5 w-3.5" /> : <Unplug className="h-3.5 w-3.5" />}
          {connected ? "Verbunden" : "Nicht verbunden"}
        </span>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-neutral-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Status wird geladen…
        </div>
      ) : connected ? (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">
                <Facebook className="h-3.5 w-3.5 text-blue-600" /> Facebook-Seite
              </div>
              <div className="text-sm font-semibold text-neutral-900 mt-1 truncate" data-testid="meta-page-name">
                {status.facebook_connected ? (status.page_name || "Verbunden") : "Nicht verbunden"}
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">
                <Instagram className="h-3.5 w-3.5 text-pink-500" /> Instagram-Business
              </div>
              <div className="text-sm font-semibold text-neutral-900 mt-1 truncate" data-testid="meta-ig-username">
                {status.ig_username ? `@${status.ig_username}` : "Nicht verknüpft"}
              </div>
            </div>
          </div>

          {!status.has_instagram && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-[13px] text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Diese Facebook-Seite hat noch kein verknüpftes Instagram-<strong>Business</strong>-Konto.
                Bitte das Instagram-Konto auf „Business" stellen und mit der Seite verbinden, dann erneut verbinden.
              </span>
            </div>
          )}

          {!status.facebook_connected && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-[13px] text-blue-900" data-testid="fb-connect-hint">
              <p className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span><strong>Facebook-Seite noch nicht verbunden</strong> — Beiträge werden aktuell nur auf Instagram ausgespielt. Verbinde die zu diesem Kanal gehörende Facebook-Seite, damit Posts auch auf Facebook erscheinen.</span></p>
              <button data-testid="fb-connect-btn" onClick={connectFacebook}
                className="mt-2.5 inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700">
                <Facebook className="h-4 w-4" /> Facebook-Seite verbinden
              </button>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {status.facebook_connected && (
              <button
                data-testid="fb-switch-page-btn"
                onClick={connectFacebook}
                className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-blue-700"
              >
                <Facebook className="h-4 w-4" /> Facebook-Seite wechseln
              </button>
            )}
            <button
              data-testid="meta-reconnect-btn"
              onClick={connect}
              disabled={status.instagram_reconnect_available === false}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plug className="h-4 w-4" /> Neu verbinden
            </button>
            <button
              data-testid="meta-disconnect-btn"
              onClick={disconnect}
              disabled={working}
              className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />} Trennen
            </button>
          </div>
          {status.instagram_reconnect_available === false && (
            <p className="text-xs text-neutral-500">
              Die bestehende Instagram-Verbindung ist aktiv und wird automatisch erneuert. Eine manuelle
              Neuverbindung ist erst nötig, wenn die separate Instagram-App serverseitig hinterlegt ist.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-5">
          <button
            data-testid="meta-connect-btn"
            onClick={connect}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 transition"
          >
            <Plug className="h-4 w-4" /> Instagram & Facebook verbinden
          </button>
          <div className="mt-4 rounded-lg bg-neutral-50 border border-neutral-200 px-4 py-3 text-[13px] text-neutral-600">
            <span className="font-semibold text-neutral-800">Voraussetzung:</span> Ein Instagram-<strong>Business</strong>-Konto
            (kein Creator-Konto) verknüpft mit einer Facebook-Seite. Beim Verbinden wirst du sicher zu Meta weitergeleitet —
            wir erhalten <strong>nie</strong> dein Passwort, nur ein widerrufbares Zugriffs-Token.
          </div>
        </div>
      )}
    </section>
  );
}

const CRED_PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "text-pink-600" },
  { key: "facebook", label: "Facebook", Icon: Facebook, color: "text-blue-600" },
  { key: "tiktok", label: "TikTok", Icon: Music2, color: "text-neutral-900" },
];

function CredentialRow({ accountKey, platform, cred, isAdmin, onSaved }) {
  const [username, setUsername] = useState(cred?.username || "");
  const [password, setPassword] = useState(cred?.password || "");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setUsername(cred?.username || ""); setPassword(cred?.password || ""); }, [cred?.username, cred?.password]);

  const copy = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} kopiert`); }
    catch { toast.info(val, { duration: 8000 }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/social-credentials", { account_key: accountKey, platform: platform.key, username, password });
      toast.success(username || password ? "Zugangsdaten gespeichert" : "Eintrag entfernt");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin && !cred) return null;

  return (
    <div data-testid={`cred-row-${accountKey}-${platform.key}`} className="flex flex-wrap items-center gap-2 py-2">
      <span className={`flex items-center gap-1.5 text-xs font-semibold w-24 shrink-0 ${platform.color}`}>
        <platform.Icon className="h-4 w-4" /> {platform.label}
      </span>
      {isAdmin ? (
        <>
          <input data-testid={`cred-user-${accountKey}-${platform.key}`} value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="Benutzername / E-Mail" className="h-9 px-3 rounded-lg border border-neutral-300 text-sm flex-1 min-w-[140px]" />
          <div className="relative flex-1 min-w-[140px]">
            <input data-testid={`cred-pass-${accountKey}-${platform.key}`} type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort" className="h-9 pl-3 pr-9 rounded-lg border border-neutral-300 text-sm w-full" />
            <button onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button data-testid={`cred-save-${accountKey}-${platform.key}`} onClick={save} disabled={saving}
            className="h-9 px-3 rounded-lg bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
          </button>
        </>
      ) : (
        <>
          <button onClick={() => copy(cred.username, "Benutzername")} title="Benutzername kopieren"
            className="flex items-center gap-1.5 text-sm text-neutral-700 font-medium px-2.5 py-1.5 rounded-lg hover:bg-neutral-100 min-w-0">
            <span className="truncate">{cred.username || "—"}</span> <Copy className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
          </button>
          <button data-testid={`cred-showpass-${accountKey}-${platform.key}`} onClick={() => setShow((v) => !v)} title="Passwort anzeigen"
            className="flex items-center gap-1.5 text-sm font-mono px-2.5 py-1.5 rounded-lg hover:bg-neutral-100 text-neutral-700">
            {show ? cred.password : "••••••••"} {show ? <EyeOff className="h-3.5 w-3.5 text-neutral-400" /> : <Eye className="h-3.5 w-3.5 text-neutral-400" />}
          </button>
          <button onClick={() => copy(cred.password, "Passwort")} title="Passwort kopieren"
            className="h-8 w-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-neutral-700">
            <Copy className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

function SocialCredentialsCard({ isAdmin }) {
  const [accounts, setAccounts] = useState([]);
  const [creds, setCreds] = useState([]);

  const load = useCallback(() => {
    api.get("/accounts").then(({ data }) => setAccounts(data));
    api.get("/social-credentials").then(({ data }) => setCreds(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const credFor = (key, platform) => creds.find((c) => c.account_key === key && c.platform === platform);
  const visible = isAdmin ? accounts : accounts.filter((a) => CRED_PLATFORMS.some((p) => credFor(a.key, p.key)));

  if (!isAdmin && visible.length === 0) return null;

  return (
    <section data-testid="social-credentials-card" className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-neutral-400" /> Social-Media-Zugänge
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        {isAdmin
          ? "Hinterlege hier die Login-Daten der Instagram-, Facebook- & TikTok-Accounts. Creator sehen nur die Zugänge ihrer zugewiesenen Kanäle und können sie selbstständig kopieren."
          : "Login-Daten für die Social-Media-Accounts deiner Kanäle — zum Kopieren antippen."}
      </p>
      <div className="mt-4 space-y-4">
        {visible.map((a) => (
          <div key={a.key} data-testid={`cred-account-${a.key}`} className="rounded-lg border border-neutral-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">{a.name}</div>
            <div className="divide-y divide-neutral-100">
              {CRED_PLATFORMS.map((p) => (
                <CredentialRow key={p.key} accountKey={a.key} platform={p} cred={credFor(a.key, p.key)} isAdmin={isAdmin} onSaved={load} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-neutral-400 flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" /> Nur für dein Team sichtbar — Creator sehen ausschließlich die Kanäle, die ihnen zugewiesen sind.
      </p>
    </section>
  );
}

function PushSettingsCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    const support = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    let subscribed = false;
    if (support) {
      try {
        const reg = await navigator.serviceWorker.ready;
        subscribed = !!(await reg.pushManager.getSubscription());
      } catch { /* noop */ }
    }
    setSt({ support, permission: support ? Notification.permission : "unsupported", subscribed, ios, standalone });
  };
  useEffect(() => { refresh(); }, []);

  if (!st) return null;
  const active = st.support && st.permission === "granted" && st.subscribed;
  const blocked = st.permission === "denied";
  const iosNotInstalled = st.ios && !st.standalone;

  const activate = async () => {
    setBusy(true);
    await ensurePushSubscription();
    await refresh();
    setBusy(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/push/test");
      toast.success("Test gesendet — die Benachrichtigung sollte gleich auf deinem Gerät erscheinen 📳");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6" data-testid="push-settings-card">
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <Bell className="h-5 w-5 text-neutral-400" /> Push-Benachrichtigungen
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        Aufgaben, Chat-Nachrichten und Freigabe-Anfragen kommen wie bei WhatsApp direkt aufs Gerät — auch bei geschlossener App.
      </p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span
          data-testid="push-status-badge"
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
            active ? "bg-emerald-100 text-emerald-800" : blocked ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
          }`}
        >
          {active ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          {active ? "Aktiv auf diesem Gerät" : blocked ? "Vom Gerät blockiert" : st.support ? "Noch nicht aktiviert" : "Von diesem Browser nicht unterstützt"}
        </span>

        {!active && st.support && !blocked && (
          <button
            data-testid="push-activate-btn"
            onClick={activate}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Jetzt aktivieren
          </button>
        )}

        {active && (
          <button
            data-testid="push-test-btn"
            onClick={sendTest}
            disabled={testing}
            className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Test-Benachrichtigung senden
          </button>
        )}
      </div>

      {iosNotInstalled && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-[13px] text-amber-900" data-testid="push-ios-hint">
          <span className="font-semibold">iPhone/iPad:</span> Push funktioniert nur, wenn die App am Homescreen installiert ist.
          In Safari: <b>Teilen-Symbol → „Zum Home-Bildschirm"</b> — danach die App vom Homescreen öffnen und hier „Jetzt aktivieren" tippen.
        </div>
      )}
      {blocked && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-[13px] text-rose-900" data-testid="push-blocked-hint">
          Benachrichtigungen wurden für diese App blockiert. Bitte in den <b>Geräte-/Browser-Einstellungen</b> für diese Website wieder erlauben und die Seite neu laden.
        </div>
      )}
      {active && (
        <p className="mt-3 text-[12px] text-neutral-400">
          Tipp: Jedes Gerät (Handy, Tablet, PC) muss einmal aktiviert werden — am besten auf dem Handy in der installierten App wiederholen.
        </p>
      )}
    </section>
  );
}

function VersionCard() {
  const [busy, setBusy] = useState(false);
  const src = Array.from(document.scripts).map((s) => s.src).find((s) => s.includes("/static/js/main."));
  const build = src ? (src.match(/main\.([a-z0-9]+)\.js/)?.[1] || "unbekannt") : "Entwicklung (Live-Reload)";

  const forceUpdate = async () => {
    setBusy(true);
    try {
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      const reg = await navigator.serviceWorker?.getRegistration?.();
      await reg?.unregister?.();
    } catch { /* noop */ }
    window.location.reload();
  };

  return (
    <section className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6" data-testid="version-card">
      <h2 className="font-head font-semibold text-neutral-900 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-neutral-400" /> App-Version
      </h2>
      <p className="text-sm text-neutral-500 mt-1">
        Aktueller Stand auf diesem Gerät: <span className="font-mono font-semibold text-neutral-800" data-testid="build-version">{build}</span>
      </p>
      <p className="text-[12px] text-neutral-400 mt-1">
        Ändert sich diese Kennung nach einem Deployment nicht, lädt dein Gerät noch die alte Version — dann hilft der Knopf:
      </p>
      <button
        data-testid="force-update-btn"
        onClick={forceUpdate}
        disabled={busy}
        className="mt-3 inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} Neueste Version laden
      </button>
    </section>
  );
}
